import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

export default function Notices() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleBack = (e: React.MouseEvent) => {
        e.preventDefault();
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans selection:bg-primary selection:text-white">
            <SEO
                title={t('legal.notices.title')}
                description={t('legal.notices.seo_description')}
                canonical="/legal/notices"
            />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </Link>
                        <Link to="/" className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <a
                            href="/"
                            onClick={handleBack}
                            className="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-primary transition-colors"
                        >
                            <ArrowLeft size={16} />
                            {t('legal.nav.back')}
                        </a>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="pt-32 pb-20 px-6 max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-8">{t('legal.notices.title')}</h1>
                <p className="text-text-muted mb-12">{t('legal.notices.updated')}</p>

                <div className="space-y-12 text-lg text-text-muted leading-relaxed">
                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.notices.section1.title')}</h2>
                        <p>
                            {t('legal.notices.section1.content')}
                        </p>
                        <ul className="list-none mt-4 space-y-1 pl-4 border-l-4 border-primary/20">
                            <li><strong>{t('legal.notices.section1.company')}</strong> NNT Inc.</li>
                            <li><strong>{t('legal.notices.section1.address')}</strong> 2104 Rue du Pere-Lalemant, Saguenay, QC, G7X 6H3, Canada</li>
                            <li><strong>{t('legal.notices.section1.email')}</strong> citoyenpro@nnt-quebec.com</li>
                            <li><strong>{t('legal.notices.section1.director')}</strong> Derrick Nana Ndokep</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.notices.section2.title')}</h2>
                        <p>
                            {t('legal.notices.section2.content')}
                        </p>
                        <ul className="list-none mt-4 space-y-1 pl-4 border-l-4 border-primary/20">
                            <li><strong>{t('legal.notices.section2.host')}</strong> Hostinger International Ltd.</li>
                            <li><strong>{t('legal.notices.section2.host_address')}</strong> 61 Lordou Vironos Street, 6023 Larnaca, Chypre</li>
                            <li><strong>{t('legal.notices.section2.website')}</strong> https://www.hostinger.com</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.notices.section3.title')}</h2>
                        <p>
                            {t('legal.notices.section3.content')}
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-dark-card border-t border-dark-border py-12 text-center">
                <p className="text-text-muted text-sm">{t('legal.footer.copyright')}</p>
            </footer>
        </div>
    );
}
